import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { PageAction } from '@makyoapp/makyo-common'

import constate from '@/lib/constate'
import { toSdkOverlay, toSdkOverlayList } from '@/adapters/__project__/BuilderOverlayAdapter'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useReqError } from '@/hooks/useReqError'
import { EVENT_KEYS } from '@/shared/custom-event'
import { BuilderOverlay } from '@/shared/overlay'
import { genNewOverlay } from '@/utils/builder/overlay'

const [AppOverlaysProvider, useAppOverlaysContext] = constate('AppOverlaysContext', () => {
  // access global builder context
  const { appClient, overlays, setOverlays, activeOverlays, setActiveOverlays, updateVersion } = useAppBuilderContext()

  // Local State
  const [overlayAction, setOverlayAction] = useState<PageAction | undefined>(undefined)
  const [activeOverlayId, setActiveOverlayId] = useState('')

  const { activeOverlayIndex, activeOverlayInternalIdx, activeDetailOverlay } = useMemo(() => {
    let activeOverlayIndex = -1
    let activeOverlayInternalIdx = -1
    let activeDetailOverlay = undefined

    let lastOverlay = activeOverlays[activeOverlays.length - 1]

    if (overlays && lastOverlay) {
      if (lastOverlay.tableId && lastOverlay.visible) {
        activeOverlayInternalIdx = activeOverlays.length - 1
        activeOverlayIndex = activeOverlays.length - 1
        activeDetailOverlay = lastOverlay
      } else {
        let currentActiveIndex = overlays.findIndex((o) => o.id === lastOverlay.id)
        activeOverlayInternalIdx = activeOverlays.length - 1

        if (currentActiveIndex > -1) {
          activeOverlayIndex = currentActiveIndex
        }
      }
    }

    return { activeOverlayIndex, activeOverlayInternalIdx, activeDetailOverlay }
  }, [activeOverlays, overlays])

  const showOverlay = useCallback(
    (overlayId: string) => {
      if (!overlays) return

      let newOverlay = overlays.find((o) => o.id === overlayId)

      if (newOverlay) {
        newOverlay.visible = true
      }
      setActiveOverlays((prev) => {
        let newCopy = [...prev]
        let foundIdx = newCopy.findIndex((o) => o?.id === overlayId)
        if (foundIdx > -1) {
          delete newCopy[foundIdx]
        }
        if (newOverlay) {
          newCopy.push(newOverlay)
        }
        return newCopy
      })
    },
    [overlays, setActiveOverlays]
  )

  const showDetailScreenOverlay = useCallback(
    (overlay: BuilderOverlay) => {
      setActiveOverlays((prev) => [
        ...prev,
        {
          ...overlay,
          visible: true,
        },
      ])
    },
    [setActiveOverlays]
  )

  const printAPIError = useReqError()

  const activeOverlay = activeOverlays.length ? activeOverlays[activeOverlayInternalIdx] || undefined : undefined
  const activeOverlayClient = appClient.overlaysMap.get(activeOverlayId)

  const addOverlay = useCallback(async () => {
    try {
      const newOverlay = genNewOverlay(overlays as BuilderOverlay[], `Overlay ${(overlays ?? [])?.length + 1}`)
      await appClient.AddOverlay(toSdkOverlay(newOverlay))
      updateVersion()
    } catch (err) {
      printAPIError(err as ErrorResponse)
    }
  }, [overlays, appClient, updateVersion, printAPIError])

  const updateOverlay = useCallback(
    async (idx: number, cb: (prev: BuilderOverlay) => void) => {
      if (!!activeDetailOverlay) {
        const copy = [...activeOverlays]
        cb(copy[idx])
        setActiveOverlays(copy)
      } else {
        const copy = [...overlays]
        cb(copy[idx])

        const overlay = toSdkOverlay(copy[idx])

        try {
          const currentOverlay = appClient.overlaysMap?.get(overlay._id)
          if (currentOverlay) {
            await currentOverlay.Update(overlay)
            updateVersion()
          }
        } catch (err) {
          printAPIError(err as ErrorResponse)
        }
      }
    },
    [
      activeDetailOverlay,
      activeOverlays,
      setActiveOverlays,
      overlays,
      appClient.overlaysMap,
      updateVersion,
      printAPIError,
    ]
  )

  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateOverlayList = useCallback(
    async (cb: (prev: BuilderOverlay[]) => BuilderOverlay[]) => {
      if (!overlays) return

      const next = [...overlays]
      const nextNew = cb(next)

      try {
        setOverlays(nextNew)
        const sdkOverlays = toSdkOverlayList(nextNew)
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current)
        }

        debounceTimeout.current = setTimeout(async () => {
          await appClient.ReorderOverlay(sdkOverlays)
        }, 1500)
      } catch (err) {
        printAPIError(err as ErrorResponse)
      }
    },
    [appClient, overlays, printAPIError, setOverlays]
  )

  const deleteOverlay = async (id: string) => {
    let activeOverlay = activeOverlays[activeOverlays.length - 1]

    if (activeOverlay?.id === id) closeOverlay(id)

    try {
      const newOverlays = overlays?.filter((o) => o.id !== id)
      setOverlays(newOverlays)
      await appClient.DeleteOverlay(id)
      updateVersion()
    } catch (e) {
      printAPIError(e as ErrorResponse)
    }
  }

  const closeOverlay = useCallback(
    (overlayId: string) => {
      setActiveOverlays((prev) => {
        let newCopy = [...prev]
        let idx = newCopy.findIndex((o) => o.id === overlayId)
        if (idx > -1) {
          newCopy[idx].visible = false
        }
        return newCopy
      })

      // fire custom window event
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.OVERLAY_CLOSE, { detail: overlayId }))
    },
    [setActiveOverlays]
  )

  const closeOverlayWithPromise = useCallback(
    (overlayId: string): Promise<void> => {
      return new Promise((resolve) => {
        closeOverlay(overlayId)
        setTimeout(resolve, 100)
      })
    },
    [closeOverlay]
  )

  const closeAllOverlays = useCallback(() => {
    if (!activeOverlays.length) return

    return activeOverlays.reduce<Promise<void>>(
      (acc, overlay) => acc.then(() => closeOverlayWithPromise(overlay.id)),
      Promise.resolve()
    )
  }, [activeOverlays, closeOverlayWithPromise])

  const changeDetailsOverlayIndex = useCallback(
    (cb: (prev: BuilderOverlay) => void) => {
      let isDetailOverlay = !!activeDetailOverlay && activeOverlayIndex > -1 && activeOverlayInternalIdx > -1
      if (!isDetailOverlay) return

      setActiveOverlays((prev) => {
        let copy = [...prev]
        cb(copy[activeOverlayInternalIdx])
        return copy
      })
    },
    [activeDetailOverlay, activeOverlayIndex, activeOverlayInternalIdx, setActiveOverlays]
  )

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  // to sync and update activeOverlays whenever changes happen in overlays
  const syncActiveOverlays = useCallback(() => {
    setActiveOverlays((prevActiveOverlays) => {
      // Map through the previous activeOverlays and update if an overlay with the same id exists in the new overlays
      return prevActiveOverlays.map((activeOverlay) => {
        const updatedOverlay = overlays.find((o) => o.id === activeOverlay.id)
        return updatedOverlay ? { ...activeOverlay, ...updatedOverlay } : activeOverlay
      })
    })
  }, [overlays, setActiveOverlays])

  useEffect(() => {
    syncActiveOverlays()
  }, [overlays, syncActiveOverlays])

  return {
    overlays,
    showOverlay,
    activeOverlays,
    activeOverlayClient,
    addOverlay,
    updateOverlay,
    updateOverlayList,
    deleteOverlay,
    closeOverlay,
    closeOverlayWithPromise,
    closeAllOverlays,
    setActiveOverlayId,
    activeOverlayId,
    setOverlayAction,
    overlayAction,
    activeOverlay,
    showDetailScreenOverlay,
    changeDetailsOverlayIndex,
    setOverlays,
    activeOverlayIndex,
  }
})

export { AppOverlaysProvider, useAppOverlaysContext }
